import { ComboboxItem } from '@oncolens/oncolens-ui/lib/components/FormControls/Combobox/Combobox';
import { mapToListboxItems } from '@oncolens/oncolens-ui/lib/components/FormControls/Listbox';
import { PagedApiResponse, Pagination } from '@oncolens/oncolens-ui/lib/dto/api/ApiResponse';
import { useMemo } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import client from '../../../utils/httpClient';
import { BehaviorConfigKeys } from './behavior-configs.keys';

export interface BehaviorConfigDto {
  id: string;
  type: string;
  startDate?: Date;
  endDate?: Date;
  displayPages: string[];
  userSpecialties: string[];
  organizationIds: string[];
  assignAllOrganizations: boolean;
  message: string;
  groupRefCode: string;
  status: BehaviorConfigStatus;
  diseaseLocationCriteria: string[];
  diseaseTypeCriteria: string[];
  diseaseSettingCriteria: string[];
  isCme?: boolean;
}

export interface BehaviorConfigForManipulationDto {
  type: string;
  startDate?: Date;
  endDate?: Date;
  displayPages: string[];
  userSpecialties: string[];
  organizationIds: string[] | null;
  assignAllOrganizations: boolean;
  message: string;
  groupRefCode?: string | null;
  status: BehaviorConfigStatus;
  diseaseLocationCriteria?: string[];
  diseaseTypeCriteria?: string[];
  diseaseSettingCriteria?: string[];
  isCme?: boolean;
}

export interface BehaviorConfigFormValues {
  organizationIds: ComboboxItem[] | null;
  type: string;
  startDate?: Date;
  endDate?: Date;
  displayPages: string[] | null;
  userSpecialties: ComboboxItem[] | null;
  assignAllOrganizations: boolean;
  message: string;
  groupRefCode?: string | null;
  status: BehaviorConfigStatus;
  diseaseLocationCriteria?: ComboboxItem[] | null;
  diseaseTypeCriteria?: ComboboxItem[] | null;
  diseaseSettingCriteria?: ComboboxItem[] | null;
  isCme?: boolean;
  dateRangeFilters: BehaviorConfigFormValuesDateRangeFilters;
}

export interface BehaviorConfigFormValuesDateRangeFilters {
  startDate?: Date;
  endDate?: Date;
}

export enum BehaviorConfigStatus {
  Active = 'Active',
  Inactive = 'Inactive',
}

export type BehaviorConfigUserSpecialtyDto = {
  specialityId: number;
  specialityName: string;
};

function getBehaviorConfigs(queryString: string) {
  const query = queryString ? `?${queryString}` : '';
  return client.get(`/behaviorconfigs${query}`).then((response) => {
    const data = response.data as BehaviorConfigDto[];

    return {
      data,
      pagination: JSON.parse(response.headers['x-pagination']) as Pagination,
    } as PagedApiResponse<BehaviorConfigDto>;
  });
}

function getBehaviorConfigById(id: string) {
  return client
    .get(`/behaviorconfigs/${id}`)
    .then((response) => response.data as BehaviorConfigDto);
}

function useBehaviorConfigById(id: string, options?: UseQueryOptions<BehaviorConfigDto>) {
  return useQuery(BehaviorConfigKeys.detail(id), () => getBehaviorConfigById(id), options);
}

function updateBehaviorConfig(id: string, data: BehaviorConfigForManipulationDto) {
  return client.put(`/behaviorconfigs/${id}`, data);
}

function createBehaviorConfig(data: BehaviorConfigForManipulationDto) {
  return client.post('/behaviorconfigs', data);
}

function getDisplayPages() {
  return client
    .get(`/behaviorconfigs/getdisplaypages`)
    .then((response) => response.data as string[]);
}

function useDisplayPages(options?: UseQueryOptions<string[]>) {
  return useQuery(BehaviorConfigKeys.displayPages(), () => getDisplayPages(), options);
}

const DISPLAY_PAGE_LABELS: Record<string, string> = {
  v2_conferences: 'V2 Conferences',
  v2_conference_agenda: 'V2 Conference Agenda',
};

function getDisplayPageLabel(value: string): string {
  return DISPLAY_PAGE_LABELS[value] ?? value;
}

function useDisplayPagesData() {
  const { data } = useDisplayPages();
  return useMemo(
    () => mapToListboxItems(data || [], getDisplayPageLabel, (value) => value),
    [data],
  );
}

const getUserSpecialties = async () => {
  const response = await client.get(`/behaviorconfigs/getuserspecialties`);
  return response.data as string[];
};

const useUserSpecialties = () =>
  useQuery(BehaviorConfigKeys.specialties(), () => getUserSpecialties());

function useUserSpecialtiesData() {
  const { data } = useUserSpecialties();
  return useMemo(
    () =>
      mapToListboxItems(
        data || [],
        (value) => value,
        (value) => value,
      ),
    [data],
  );
}

export {
  getBehaviorConfigs,
  useBehaviorConfigById,
  updateBehaviorConfig,
  createBehaviorConfig,
  useDisplayPages,
  useDisplayPagesData,
  useUserSpecialtiesData,
};
